import { useState, useEffect, useRef } from 'react';
import Highcharts from 'highcharts/highstock';
import HighchartsReact from 'highcharts-react-official';
import { FaFileCsv, FaFileCode } from 'react-icons/fa';

// Configure Highcharts to use UTC for data storage but display in Czech timezone
// Czech Republic is UTC+1 in winter (CET) and UTC+2 in summer (CEST)
// Highcharts' timezoneOffset is the offset in minutes from UTC
// For Czech Republic: UTC+1 = -60 minutes, UTC+2 = -120 minutes
// We calculate the current offset based on DST
const getCzechTimezoneOffset = () => {
  const now = new Date();
  // Check if we're in DST (summer time)
  // In Czech Republic, DST runs from last Sunday in March to last Sunday in October
  const year = now.getFullYear();
  const march = new Date(year, 2, 31); // March 31
  const october = new Date(year, 9, 31); // October 31
  const lastSundayMarch = new Date(march.setDate(31 - march.getDay()));
  const lastSundayOctober = new Date(october.setDate(31 - october.getDay()));

  // DST is active from last Sunday in March to last Sunday in October
  const isDST = now >= lastSundayMarch && now < lastSundayOctober;

  // Return offset in minutes: -60 for UTC+1 (winter), -120 for UTC+2 (summer)
  return isDST ? -120 : -60;
};

Highcharts.setOptions({
  time: {
    useUTC: true,
    // Set timezone offset for Czech Republic
    // This ensures UTC timestamps are displayed in Czech local time
    timezoneOffset: getCzechTimezoneOffset(),
  },
});

interface ChartProps {
  userDevices: any[];
  type: string;
  unit: string;
  title: string;
}

const Chart = ({ userDevices, type, unit, title }: ChartProps) => {
  const [chartData, setChartData] = useState<[number, number][]>([]);
  const chartRef = useRef<any>(null);
  const [allRawData, setAllRawData] = useState<
    Array<{ timestamp: number; value: number; deviceName?: string; deviceId?: string }>
  >([]);

  useEffect(() => {
    const rawDataWithDevice = userDevices.flatMap(
      (device) =>
        device.sensors?.flatMap((sg: any) =>
          sg.data
            .filter((d: any) => d.type === type)
            .map((d: any) => {
              // KRITICKÉ: Použij aggregatedAt pro agregované záznamy, createdAt pro raw data
              // Backend vrací agregované záznamy s aggregatedAt jako správným časem měření
              // createdAt u agregovaných záznamů je čas vytvoření agregace (novější!)
              const timestamp = new Date(sg.aggregatedAt || sg.createdAt).getTime();
              return {
                timestamp,
                value: d.value,
                deviceName: device.settings?.name || device.location || device._id,
                deviceId: device._id,
              };
            }),
        ) || [],
    );

    const rawData = rawDataWithDevice.map((d) => [d.timestamp, d.value] as [number, number]);

    // Store all raw data for export
    setAllRawData(rawDataWithDevice);

    if (rawData.length === 0) {
      setChartData([]);
      return;
    }

    // Seskupení podle timestampu a průměrování hodnot se stejným časem
    const grouped: Record<number, number[]> = {};
    rawData.forEach(([timestamp, value]) => {
      // timestamp je již number (ms), není potřeba konverze
      if (!grouped[timestamp]) grouped[timestamp] = [];
      grouped[timestamp].push(value);
    });

    // Vypočítej průměr pro každý timestamp a seřaď podle času (ascending)
    const data = Object.entries(grouped)
      .map(([k, v]) => [
        parseInt(k),
        v.reduce((a, b) => a + b, 0) / v.length,
      ] as [number, number])
      .sort((a, b) => a[0] - b[0]); // Seřaď podle timestampu vzestupně

    setChartData(data);
  }, [userDevices, type]);

  // Different colors for temperature and humidity
  const getChartColors = () => {
    if (type === 'humidity') {
      return {
        lineColor: '#4A90E2', // Blue for humidity
        fillGradient: {
          linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 },
          stops: [
            [0, '#6BB6FF'], // Light blue
            [1, 'rgba(107, 182, 255, 0)'],
          ],
        },
        markerFill: '#4A90E2',
      };
    } else {
      return {
        lineColor: '#FF6B6B', // Red/Orange for temperature
        fillGradient: {
          linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 },
          stops: [
            [0, '#FF8E8E'], // Light red/orange
            [1, 'rgba(255, 142, 142, 0)'],
          ],
        },
        markerFill: '#FF6B6B',
      };
    }
  };

  const colors = getChartColors();

  // Find scheduled humidity for a given timestamp and device using historical data
  const getScheduledHumidity = async (
    timestamp: number,
    deviceId?: string,
    historicalSchedules?: any[],
  ): Promise<number | null> => {
    if (!deviceId) return null;

    // Use historical schedules if provided, otherwise fallback to current schedules
    const schedulesToCheck = historicalSchedules || [];

    // Find ALL schedules that match this timestamp (not just first one)
    const matchingSchedules = schedulesToCheck.filter((schedule: any) => {
      if (schedule.deviceId?.toString() !== deviceId?.toString()) return false;
      const from = new Date(schedule.from).getTime();
      const to = new Date(schedule.to).getTime();

      // Check if timestamp falls within this schedule
      return timestamp >= from && timestamp <= to;
    });

    if (matchingSchedules.length === 0) {
      return null;
    }

    // If multiple schedules match (overlapping), prefer the one with closer 'from' time
    // This gives priority to more recent schedule settings
    const closestSchedule = matchingSchedules.reduce((closest, current) => {
      const closestFromDiff = Math.abs(new Date(closest.from).getTime() - timestamp);
      const currentFromDiff = Math.abs(new Date(current.from).getTime() - timestamp);
      return currentFromDiff < closestFromDiff ? current : closest;
    });

    return closestSchedule.hum;
  };

  const getVisibleData = async () => {
    const chartInstance = chartRef.current?.chart;
    if (!chartInstance || allRawData.length === 0) {
      return null;
    }

    // Get the current visible range from the chart
    const xAxis = chartInstance.xAxis[0];
    const min = xAxis.min || Math.min(...allRawData.map((d) => d.timestamp));
    const max = xAxis.max || Math.max(...allRawData.map((d) => d.timestamp));

    // Fetch historical schedules for all devices in the visible range
    const uniqueDeviceIds = [...new Set(allRawData.map((d) => d.deviceId))].filter(Boolean);

    const historicalSchedulesPromises = uniqueDeviceIds.map(async (deviceId) => {
      try {
        const from = new Date(min).toISOString();
        const to = new Date(max).toISOString();
        const url = `${process.env.REACT_APP_BACKEND_URL}/api/schedule-history/${deviceId}/effective?from=${from}&to=${to}`;

        const response = await fetch(url, {
          credentials: 'include',
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result = await response.json();
        return result.map((s: any) => ({ ...s, deviceId }));
      } catch (error) {
        console.error(`Failed to fetch schedules for device ${deviceId}:`, error);
        return [];
      }
    });

    const historicalSchedulesArrays = await Promise.all(historicalSchedulesPromises);
    const historicalSchedules = historicalSchedulesArrays.flat();

    console.log('📊 Loaded', historicalSchedules.length, 'historical schedules for export');

    // Filter data to visible range and add scheduled humidity
    const visibleDataPromises = allRawData
      .filter((d) => d.timestamp >= min && d.timestamp <= max)
      .map(async (d) => ({
        ...d,
        scheduledHumidity: await getScheduledHumidity(d.timestamp, d.deviceId, historicalSchedules),
      }));

    const visibleData = await Promise.all(visibleDataPromises);

    // Sort by timestamp
    visibleData.sort((a, b) => a.timestamp - b.timestamp);

    return { visibleData, min, max };
  };

  const exportAsCSV = async () => {
    const result = await getVisibleData();
    if (!result) {
      alert('No data to export');
      return;
    }

    const { visibleData, min, max } = result;

    // Create CSV content with English headers
    const headers = [
      'Date and Time',
      'Device',
      type === 'humidity' ? 'Humidity (%)' : 'Temperature (°C)',
      'Scheduled Humidity (%)',
    ];
    const csvRows = [
      headers.join(','),
      ...visibleData.map((d) => {
        const date = new Date(d.timestamp).toLocaleString('en-US', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          hour12: false,
        });
        const value = d.value.toFixed(2);
        const scheduledHum = d.scheduledHumidity !== null ? d.scheduledHumidity.toString() : 'N/A';
        return `"${date}","${d.deviceName || 'N/A'}","${value}","${scheduledHum}"`;
      }),
    ];

    const csvContent = csvRows.join('\n');
    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    const dateRange = `${new Date(min).toLocaleDateString('en-US').replace(/\//g, '-')}_${new Date(max).toLocaleDateString('en-US').replace(/\//g, '-')}`;
    link.setAttribute('href', url);
    // Use 'type' (humidity/temperature) instead of 'title' (Vlhkost/Teplota)
    link.setAttribute('download', `${type}_${dateRange}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const exportAsJSON = async () => {
    const result = await getVisibleData();
    if (!result) {
      alert('No data to export');
      return;
    }

    const { visibleData, min, max } = result;

    // Create JSON structure with English labels
    const jsonData = {
      title: title,
      type: type,
      unit: unit,
      dateRange: {
        from: new Date(min).toISOString(),
        to: new Date(max).toISOString(),
      },
      data: visibleData.map((d) => ({
        timestamp: new Date(d.timestamp).toISOString(),
        dateTime: new Date(d.timestamp).toLocaleString('en-US', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          hour12: false,
        }),
        deviceName: d.deviceName || 'N/A',
        value: d.value,
        scheduledHumidity: d.scheduledHumidity !== null ? d.scheduledHumidity : null,
      })),
    };

    const jsonContent = JSON.stringify(jsonData, null, 2);
    const blob = new Blob([jsonContent], { type: 'application/json;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    const dateRange = `${new Date(min).toLocaleDateString('en-US').replace(/\//g, '-')}_${new Date(max).toLocaleDateString('en-US').replace(/\//g, '-')}`;
    link.setAttribute('href', url);
    // Use 'type' (humidity/temperature) instead of 'title' (Vlhkost/Teplota)
    link.setAttribute('download', `${type}_${dateRange}.json`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const options: any = {
    chart: {
      type: 'area',
      height: 500,
      backgroundColor: '#ffffff',
      borderRadius: 12,
      shadow: {
        color: 'rgba(0, 0, 0, 0.1)',
        offsetX: 0,
        offsetY: 2,
        opacity: 0.3,
        width: 4,
      },
    },
    credits: {
      enabled: false,
    },
    title: {
      text: title,
      style: {
        fontSize: '20px',
        fontWeight: '600',
        color: type === 'humidity' ? '#4A90E2' : '#FF6B6B',
      },
    },
    rangeSelector: {
      selected: 0,
      buttons: [
        {
          type: 'millisecond',
          count: 10 * 60 * 1000, // 10 minutes in milliseconds
          text: '10m',
        },
        {
          type: 'millisecond',
          count: 30 * 60 * 1000, // 30 minutes in milliseconds
          text: '30m',
        },
        {
          type: 'millisecond',
          count: 60 * 60 * 1000, // 1 hour in milliseconds
          text: '1h',
        },
        {
          type: 'millisecond',
          count: 12 * 60 * 60 * 1000, // 12 hours in milliseconds
          text: '12h',
        },
        {
          type: 'day',
          count: 1,
          text: '1d',
        },
        {
          type: 'week',
          count: 1,
          text: '1w',
        },
        {
          type: 'month',
          count: 1,
          text: '1m',
        },
        {
          type: 'month',
          count: 3,
          text: '3m',
        },
        {
          type: 'month',
          count: 6,
          text: '6m',
        },
        {
          type: 'ytd',
          text: 'YTD',
        },
        {
          type: 'all',
          text: 'All',
        },
      ],
      buttonTheme: {
        style: {
          color: '#667eea',
          fontWeight: '500',
        },
      },
    },
    xAxis: {
      type: 'datetime',
      title: { text: 'Čas' },
      gridLineColor: '#f0f0f0',
    },
    yAxis: {
      title: {
        text: unit,
        style: {
          color: type === 'humidity' ? '#4A90E2' : '#FF6B6B',
          fontWeight: '600',
        },
      },
      gridLineColor: '#f0f0f0',
    },
    tooltip: {
      valueDecimals: 2,
      valueSuffix: ` ${unit}`,
      backgroundColor: 'rgba(255, 255, 255, 0.95)',
      borderColor: colors.lineColor,
      borderRadius: 8,
      shadow: true,
    },
    plotOptions: {
      area: {
        fillColor: colors.fillGradient,
        lineColor: colors.lineColor,
        marker: {
          radius: 4,
          fillColor: colors.markerFill,
          lineColor: '#ffffff',
          lineWidth: 2,
        },
        lineWidth: 3,
        states: {
          hover: {
            lineWidth: 4,
            marker: {
              radius: 5,
            },
          },
        },
        threshold: null,
      },
    },
    series: [
      {
        type: 'area',
        name: unit,
        data: chartData,
        color: colors.lineColor,
      },
    ],
  };

  return chartData.length > 0 ? (
    <div style={{ position: 'relative' }}>
      <div
        style={{
          position: 'absolute',
          top: '10px',
          right: '10px',
          zIndex: 1000,
          display: 'flex',
          gap: '8px',
        }}
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
        }}
        onMouseDown={(e) => {
          e.stopPropagation();
          e.preventDefault();
        }}
      >
        <button
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            exportAsCSV();
          }}
          onMouseDown={(e) => {
            e.stopPropagation();
            e.preventDefault();
          }}
          style={{
            padding: '10px 16px',
            backgroundColor: '#667eea',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '600',
            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
            transition: 'all 0.3s ease',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
          }}
          onMouseOver={(e) => {
            e.currentTarget.style.backgroundColor = '#764ba2';
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = '0 4px 8px rgba(0, 0, 0, 0.3)';
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.backgroundColor = '#667eea';
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.2)';
          }}
        >
          <FaFileCsv /> CSV
        </button>
        <button
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            exportAsJSON();
          }}
          onMouseDown={(e) => {
            e.stopPropagation();
            e.preventDefault();
          }}
          style={{
            padding: '10px 16px',
            backgroundColor: '#667eea',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '600',
            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
            transition: 'all 0.3s ease',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
          }}
          onMouseOver={(e) => {
            e.currentTarget.style.backgroundColor = '#764ba2';
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = '0 4px 8px rgba(0, 0, 0, 0.3)';
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.backgroundColor = '#667eea';
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.2)';
          }}
        >
          <FaFileCode /> JSON
        </button>
      </div>
      <HighchartsReact
        ref={chartRef}
        highcharts={Highcharts}
        constructorType={'stockChart'} // Highstock
        options={options}
      />
    </div>
  ) : (
    <p>Žádné data</p>
  );
};

export default Chart;
